import { DT } from "../../constants.ts";
import { Btn } from "../../net/input.ts";
import { abilIdx, ActionKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, damageBarrier, explode, hitscan, isEnemy, moveProjectile, playersInRadius } from "../../sim/combat.ts";
import { FxKind } from "../../sim/events.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, flight, launchProjectile, ownerAlive, ready, secs, simpleProjectile, spreadDir, trigger, useUlt } from "../kitlib.ts";

export const V = { fuel: 0, overdrive: 1, beamTarget: 2, ultGlow: 3 } as const;
export const PK = { Repulsor: pk(HeroId.IonMan, 0), Missile: pk(HeroId.IonMan, 1), Pulse: pk(HeroId.IonMan, 2) } as const;
export const FX = { Repulsor: fxk(HeroId.IonMan, 0), Beam: fxk(HeroId.IonMan, 1), Pulse: fxk(HeroId.IonMan, 2) } as const;

const FUEL_MAX = 120;

const def: HeroDef = {
  id: HeroId.IonMan,
  slug: "ion-man",
  name: "Ion Man",
  role: Role.Damage,
  color: "#e0482f",
  blurb: "Armored flyer with splash repulsors, a sustained chest beam, overdrive, missiles and a huge pulse cannon.",
  health: 250,
  armor: 0,
  shields: 0,
  radius: 0.42,
  height: 1.9,
  headRadius: 0.24,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 100,
  reloadTime: 1.7,
  ultCost: 3400,
  abilities: {
    [Slot.Primary]: { name: "Palm Popper", key: "LMB", description: "Fire repulsor bolts that burst on impact; every third shot is a heavier double blast.", cooldown: 0 },
    [Slot.Secondary]: { name: "Chestlight Lance", key: "RMB (hold)", description: "Channel a 25m beam that shares the suit ammo pool.", cooldown: 0 },
    [Slot.Ability1]: { name: "Skyline Burn", key: "Shift (hold)", description: "Spend flight energy for +100% flight speed.", cooldown: 0 },
    [Slot.Ability2]: { name: "Suit Redline", key: "E", description: "Gain bonus health and empower repulsors, beam and flight for 10 seconds.", cooldown: 20 },
    [Slot.Ability3]: { name: "Pigeon Storm", key: "F", description: "Launch a micro-missile volley; overdrive fires it forward.", cooldown: 8 },
    [Slot.Ultimate]: { name: "Boardroom Sun", key: "Q", description: "Launch a slow pulse cannon that scorches its path and detonates for massive damage.", cooldown: 0 },
  },
  passive: { name: "Airframe", description: "Ion Man uses sustained flight instead of ordinary ground movement while thrusters are engaged." },
};

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.fuel, FUEL_MAX);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      const od = hv(c, eid, V.overdrive);
      if (od > 0) setHv(c, eid, V.overdrive, Math.max(0, od - 1));
      setHv(c, eid, V.beamTarget, 0);
      setHv(c, eid, V.ultGlow, Math.max(0, hv(c, eid, V.ultGlow) - 1));
      const boosting = !!(input.buttons & Btn.Ability1) && (od > 0 || hv(c, eid, V.fuel) > 0);
      if (od > 0) setHv(c, eid, V.fuel, FUEL_MAX);
      else if (!boosting) setHv(c, eid, V.fuel, Math.min(FUEL_MAX, hv(c, eid, V.fuel) + 10 * DT));

      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        setHv(c, eid, V.ultGlow, 90);
        launchProjectile(sim, eid, PK.Pulse, 25, input, { radius: 0.45, seconds: 3.5 });
      }

      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        setHv(c, eid, V.overdrive, secs(10));
        c.Health.over[eid] = Math.max(c.Health.over[eid]!, 50);
        c.Health.overTicks[eid] = Math.max(c.Health.overTicks[eid]!, secs(5));
        action(sim, eid, ActionKind.Cast, secs(1.3));
      }
      if (pressed & Btn.Ability1) {
        action(sim, eid, ActionKind.Cast, 8);
      }

      if (pressed & Btn.Ability3 && ready(sim, eid, Slot.Ability3)) {
        consume(sim, eid, Slot.Ability3);
        action(sim, eid, ActionKind.Cast, 24);
        const over = hv(c, eid, V.overdrive) > 0;
        if (over) c.Abil.cd[abilIdx(eid, Slot.Ability3)] = secs(6);
        const n = over ? 18 : 16;
        const a = aim(sim, eid);
        for (let i = 0; i < n; i++) {
          const base = over ? a.dir : { x: 0, y: -1, z: 0 };
          const dir = spreadDir(base, over ? 0.13 : 0.38, c.Weapon.shots[eid]! * 131 + i * 17 + 7);
          launchProjectile(sim, eid, PK.Missile, over ? 60 : 15, input, { radius: 0.12, seconds: over ? 1.6 : 1.0, dir });
        }
      }

      if (input.buttons & Btn.Secondary) {
        const over = hv(c, eid, V.overdrive) > 0;
        if (c.Weapon.reload[eid]! <= 0 && (over || c.Weapon.ammo[eid]! > 0)) {
          if (!over) c.Weapon.ammo[eid] = Math.max(0, c.Weapon.ammo[eid]! - 10 * DT);
          action(sim, eid, ActionKind.AltFire, 2);
          const a = aim(sim, eid, 25);
          const res = hitscan(sim, eid, a.origin, a.dir, { range: 25, damage: (over ? 180 : 140) * DT, fxKind: FX.Beam, tracer: sim.tick % 5 === 0 });
          if (res.target !== undefined && sim.players.has(res.target)) setHv(c, eid, V.beamTarget, c.Net.id[res.target]!);
        }
        return;
      }

      const over = hv(c, eid, V.overdrive) > 0;
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), over)) {
        const shot = c.Weapon.shots[eid]!;
        const heavy = !over && shot % 3 === 0;
        c.Weapon.fireCd[eid] = over ? 45 : heavy ? 43 : 36;
        action(sim, eid, ActionKind.Fire, 10);
        const a = aim(sim, eid);
        launchProjectile(sim, eid, PK.Repulsor, over || heavy ? 80 : 60, input, { radius: over || heavy ? 0.2 : 0.16, seconds: 1.8, dir: spreadDir(a.dir, 0.01, shot * 23) });
        if (heavy) launchProjectile(sim, eid, PK.Repulsor, 80, input, { radius: 0.2, seconds: 1.8, dir: spreadDir(a.dir, 0.018, shot * 23 + 11) });
      }
    },
    movement(sim, eid, input, mods) {
      const over = hv(sim.c, eid, V.overdrive) > 0;
      const boosting = !!(input.buttons & Btn.Ability1) && (over || hv(sim.c, eid, V.fuel) > 0);
      const boosted = boosting && flight(sim, eid, input, mods, { speed: sim.hero(eid).speed * 2, accel: 35, vertical: 12, pitchClimb: true, fuelIdx: over ? undefined : V.fuel, drain: 15 });
      if (!boosted) flight(sim, eid, input, mods, { speed: sim.hero(eid).speed, accel: 35, vertical: 6, pitchClimb: true });
    },
  },
  projectiles: {
    [PK.Repulsor]: simpleProjectile((sim, p, owner, hit) => {
      if (!ownerAlive(sim, owner)) return;
      const over = hv(sim.c, owner, V.overdrive) > 0;
      const direct = over ? 45 : sim.c.Projectile.radius[p]! > 0.18 ? 45 : 35;
      const splash = over ? 60 : sim.c.Projectile.radius[p]! > 0.18 ? 50 : 45;
      if (hit.target !== undefined) applyDamage(sim, hit.target, direct, owner, { pos: hit.pos });
      else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, direct, owner);
      explode(sim, owner, hit.pos, { radius: over || sim.c.Projectile.radius[p]! > 0.18 ? 5 : 3, damage: splash, minDamage: splash * 0.4, fxKind: FX.Repulsor });
    }, { color: "#7ef2ff", size: 0.2, trail: true }),
    [PK.Missile]: simpleProjectile((sim, _p, owner, hit) => {
      if (ownerAlive(sim, owner)) explode(sim, owner, hit.pos, { radius: 2, damage: hv(sim.c, owner, V.overdrive) > 0 ? 15 : 20, minDamage: 8, fxKind: FxKind.Missile });
    }, { color: "#ffd36a", size: 0.12, trail: true }),
    [PK.Pulse]: {
      visual: { color: "#ff8b35", size: 0.65, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const pos = sim.posOf(p);
        if (ownerAlive(sim, owner)) {
          for (const { eid: t } of playersInRadius(sim, pos, 2.5, (e) => isEnemy(sim, owner, e), true)) applyDamage(sim, t, 300 * DT, owner, { pos });
        }
        const hit = sim.c.Projectile.ticks[p]! <= 0 ? { pos } : undefined;
        const h = hit ?? (awaitMovePulse(sim, p));
        if (h) {
          if (ownerAlive(sim, owner)) explode(sim, owner, h.pos, { radius: 10, damage: 1000, minDamage: 50, fxKind: FX.Pulse });
          sim.destroyEntity(p);
        }
      },
    },
  },
  fx: {
    [FX.Repulsor]: { color: "#7ef2ff" },
    [FX.Beam]: { color: "#b9fbff" },
    [FX.Pulse]: { color: "#ff8b35" },
  },
};

function awaitMovePulse(sim: Parameters<NonNullable<HeroModule["projectiles"]>[number]["tick"]>[0], p: number) {
  const before = sim.c.Projectile.ticks[p]!;
  const h = moveProjectile(sim, p);
  return h ?? (before <= 0 ? { pos: sim.posOf(p), map: false } : null);
}

export default module;
